import { Suspense, useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { sentenceCase } from "change-case";
// components
import { Loader, Stack } from "..";
import TitleBar from "./TitleBar";
// constants
import { APP_ROUTES } from "../../common/constants";
// hooks
import useLocales from "../../hooks/useLocales";
import { Box, Button, ButtonGroup, Card, Link, Page, Tabs, useBreakpoints } from "@shopify/polaris";
import { capitalEvery } from "../../utils/textEnhancers";
import ExportCSVModalOrders from "../../pages/Orders/components/ExportCSVModalExportCSVModalOrders";

const ORDERS_SIDE_NAV_CONFIG = [
  { to: APP_ROUTES.ORDERS.ROOT, icon: "mdi:cart-check", title: "nav.pre-orders" },
  {
    to: APP_ROUTES.ORDERS.NOTIFICATIONS,
    icon: "streamline:ringing-bell-notification",
    title: "nav.pre-order-notifications",
  },
];

const OrdersLayout = () => {
  const [showExportModal, setShowExportModal] = useState(false);

  const { translate } = useLocales();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { smUp } = useBreakpoints();

  const toggleExportModal = () => setShowExportModal(!showExportModal);

  const [selectedTab, setSelectedTab] = useState(0);
  const tabs = ORDERS_SIDE_NAV_CONFIG.map((item) => ({
    id: item.title,
    content: capitalEvery(translate(item.title)),
    accessibilityLabel: item.title,
    panelID: item.key,
    to: item.to,
  }));

  useEffect(() => {
    const currentTabIndex = ORDERS_SIDE_NAV_CONFIG.findIndex((tab) => tab.to === pathname);
    if (currentTabIndex !== -1) {
      setSelectedTab(currentTabIndex);
    }
  }, [pathname]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    navigate(ORDERS_SIDE_NAV_CONFIG[index].to);
  };

  const PAGE = {
    [APP_ROUTES.ORDERS.ROOT]: {
      heading: translate("tobetranslated.orders.orders-card-title"),
      action: (
        <ButtonGroup>
          <Link url="shopify://admin/orders?tag=In-stock" variant="primary">
            <Button>{translate("tobetranslated.orders.orders-card-title-btn-in-stock")}</Button>
          </Link>
          <Link url="shopify://admin/orders?tag=Pre-order" variant="primary">
            <Button variant="primary">{translate("tobetranslated.orders.orders-card-title-btn-pre-order")}</Button>
          </Link>
        </ButtonGroup>
      ),
    },
    [APP_ROUTES.ORDERS.NOTIFICATIONS]: {
      heading: translate("nav.pre-order-notifications"),
      action: null,
    },
  };

  const { heading, action } = PAGE[pathname] || PAGE[APP_ROUTES.ORDERS.ROOT];

  return (
    <Page fullWidth>
      <Box padding={{ xs: "400", md: "0" }}>
        <TitleBar heading={heading} action={heading === "Order" ? action : ""} />

        <Box width="100%">
          <Card>
            <Stack spacing={2} justifyContent="flex-start">
              <Stack justifyContent="space-between" alignItems="center" width="100%">
                <Box paddingBlock={smUp ? "400" : "200"}>
                  <Box className="nav-tabs-container">
                    <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
                  </Box>
                </Box>
                <Box width={smUp ? undefined : "100%"}>
                  <Button fullWidth={!smUp} onClick={toggleExportModal}>
                    {sentenceCase(translate("common.export-csv"))}
                  </Button>
                </Box>
              </Stack>
              <Suspense fallback={<Loader />}>
                <Outlet />
              </Suspense>
            </Stack>
          </Card>
        </Box>
        <ExportCSVModalOrders open={showExportModal} onClose={toggleExportModal} />
      </Box>
    </Page>
  );
};

export default OrdersLayout;
